import { EmailData } from '../types/results.types';

interface ProvenanceDisplay {
  label: string;
  sourceUrl?: string;
  sourceLabel?: string;
}

interface PersonProvenanceDisplay {
  sourceName: 'Company website' | 'BBB';
  sourceUrl: string;
  sourceLabel: string;
}

const PUBLIC_DIRECTORY_SOURCE_TYPES = new Set([
  'GOVERNMENT',
  'BBB',
  'CHAMBER',
  'INDUSTRY_ASSOCIATION',
  'NEWS',
]);

const COMPANY_WEBSITE_SOURCE_TYPES = new Set([
  'COMPANY_WEBSITE',
  'CONTACT_PAGE',
  'OTHER_WEBSITE',
]);

function formatSourceLabel(sourceUrl: string): string {
  try {
    const parsed = new URL(sourceUrl);
    return `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`;
  } catch {
    return sourceUrl;
  }
}

export function getPersonProvenance(sourceUrl?: string | null): PersonProvenanceDisplay | null {
  if (!sourceUrl) return null;
  let sourceName: PersonProvenanceDisplay['sourceName'] = 'Company website';
  try {
    const hostname = new URL(sourceUrl).hostname.toLowerCase();
    if (hostname === 'bbb.org' || hostname.endsWith('.bbb.org')) {
      sourceName = 'BBB';
    }
  } catch {}

  return {
    sourceName,
    sourceUrl,
    sourceLabel: formatSourceLabel(sourceUrl),
  };
}

export function getEmailProvenance(email: EmailData): ProvenanceDisplay | null {
  if (email.source_type && PUBLIC_DIRECTORY_SOURCE_TYPES.has(email.source_type)) {
    return {
      label: 'Found in public business directory',
      sourceUrl: email.source_url,
      sourceLabel: email.source_url ? formatSourceLabel(email.source_url) : undefined,
    };
  }

  if (email.source_type === 'PATTERN_MATCH' || email.source_type === 'GENERIC') {
    return {
      label: 'Matched using company email pattern',
      sourceUrl: email.source_url,
      sourceLabel: email.source_url ? formatSourceLabel(email.source_url) : undefined,
    };
  }

  if (
    email.source_url ||
    (email.source_type && COMPANY_WEBSITE_SOURCE_TYPES.has(email.source_type))
  ) {
    return {
      label: 'Found on company website',
      sourceUrl: email.source_url,
      sourceLabel: email.source_url ? formatSourceLabel(email.source_url) : undefined,
    };
  }

  return null;
}
